import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Mail, MapPin, Shield } from 'lucide-react';
import { siteConfig } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const Footer = () => {
  const { t } = useLanguage();
  
  const quickLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.ourWork'), path: '/programs' },
    { name: t('nav.alertMap'), path: '/alert-map', highlight: true },
    { name: t('nav.news'), path: '/news' },
    { name: t('nav.legalHelp'), path: '/legal-help' },
    { name: t('nav.partners'), path: '/partners' },
    { name: t('nav.contact'), path: '/contact' }
  ];

  return (
    <footer className="bg-stone-800 border-t border-stone-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-4">
              <img 
                src={siteConfig.logo} 
                alt="Xicano Rights Union"
                className="h-24 w-auto"
              />
            </Link>
            <div className="mb-4">
              <span className="inline-flex items-center gap-2 px-3 py-1 bg-teal-600/20 border border-teal-500/30 rounded-full text-teal-400 text-sm font-medium">
                <Shield size={14} />
                {t('footer.tagline', 'A Human Rights Organization')}
              </span>
            </div>
            <p className="text-stone-300 leading-relaxed max-w-md mb-6">
              {t('footer.description')}
            </p>
            <div className="flex items-center gap-4">
              <a
                href={siteConfig.socialLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-stone-700 hover:bg-teal-600 text-stone-300 hover:text-white rounded-lg transition-all duration-300"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
              <a
                href={siteConfig.socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-stone-700 hover:bg-teal-600 text-stone-300 hover:text-white rounded-lg transition-all duration-300"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-6">{t('common.quickLinks')}</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className={`transition-colors duration-300 ${
                      link.highlight 
                        ? 'text-red-400 hover:text-red-300 font-medium flex items-center gap-2' 
                        : 'text-stone-300 hover:text-teal-400'
                    }`}
                  >
                    {link.highlight && <span className="w-2 h-2 bg-red-400 rounded-full animate-pulse" />}
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-6">{t('common.contactUs')}</h3>
            <ul className="space-y-4">
              <li className="flex items-center gap-3 text-stone-300">
                <Mail size={18} className="text-teal-400 flex-shrink-0" />
                <a 
                  href={`mailto:${siteConfig.email}`}
                  className="hover:text-teal-400 transition-colors break-all"
                >
                  {siteConfig.email}
                </a>
              </li>
              <li className="flex items-center gap-3 text-stone-300">
                <MapPin size={18} className="text-teal-400 flex-shrink-0" />
                <span>{siteConfig.location}</span>
              </li>
            </ul>
            
            <div className="mt-8">
              <Link
                to="/report-violation"
                className="inline-block px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition-all duration-300"
              >
                {t('nav.reportViolation')}
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-stone-700">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-stone-400 text-sm">
              © {new Date().getFullYear()} Xicano Rights Union. {t('common.allRightsReserved')}
            </p>
            <p className="text-stone-400 text-sm">
              {t('common.defendingRights')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
